import { WeekNav } from '../WeekNav';
import { formatCrystalValue } from '../../lib/bossGrouping';
import type { IncomeResponse } from '../../lib/bosses';

interface AccountOption {
  accountId: number;
  label: string;
}

interface BossesHeaderProps {
  accounts: AccountOption[];
  selectedAccountId: number | null;
  onSelectAccount: (accountId: number) => void;
  weekLabel: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  isCurrentWeek: boolean;
  canGoNext: boolean;
  income: IncomeResponse | undefined;
}

/** The account summary bar: total weekly income, the 180/world cap meter, unpriced-clear count, week nav. */
export function BossesHeader({
  accounts,
  selectedAccountId,
  onSelectAccount,
  weekLabel,
  onPrevWeek,
  onNextWeek,
  onThisWeek,
  isCurrentWeek,
  canGoNext,
  income,
}: BossesHeaderProps) {
  const pct = income && income.worldCap > 0 ? Math.min(100, Math.round((income.worldWeeklyCrystalsSold / income.worldCap) * 100)) : 0;

  return (
    <div className="space-y-4 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
      <div>
        <h1 className="font-display text-xl font-semibold text-text">Bosses</h1>
        <p className="mt-1 text-sm text-text-muted">Weekly boss plans, clears and crystal income.</p>
      </div>

      {accounts.length > 1 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Account">
          {accounts.map((account) => (
            <button
              key={account.accountId}
              type="button"
              role="tab"
              aria-selected={account.accountId === selectedAccountId}
              onClick={() => onSelectAccount(account.accountId)}
              className={[
                'min-h-tap rounded-full px-4 text-sm font-semibold transition-colors',
                account.accountId === selectedAccountId
                  ? 'bg-accent text-accent-contrast'
                  : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
              ].join(' ')}
            >
              {account.label}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-bg p-3 sm:grid-cols-4">
        {/* REV15: weekly vs Black Mage split — the old single "Weekly income" figure silently
            included the monthly BM crystal, which dwarfs weekly crystals and hid the real weekly
            rate. `totalMesos` is still the sum of both cells. */}
        <div>
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Weekly income
          </p>
          <p className="text-lg font-semibold tabular-nums text-crystal" data-testid="weekly-mesos">
            {income ? formatCrystalValue(income.totalMesos - income.monthlyMesos) : '—'}
          </p>
        </div>
        <div>
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Black Mage
          </p>
          <p className="text-lg font-semibold tabular-nums text-crystal" data-testid="monthly-mesos">
            {income ? formatCrystalValue(income.monthlyMesos) : '—'}
          </p>
        </div>
        <div>
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Account crystals
            <span
              className={['ml-1 tabular-nums', income?.worldCapReached ? 'text-warning' : 'text-crystal'].join(' ')}
              data-testid="world-cap"
            >
              {income ? `${income.worldWeeklyCrystalsSold}/${income.worldCap}` : '—'}
            </span>
          </p>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-bg-elevated-hover">
            <div
              role="progressbar"
              aria-label="Account crystals sold"
              aria-valuenow={income?.worldWeeklyCrystalsSold ?? 0}
              aria-valuemin={0}
              aria-valuemax={income?.worldCap ?? 180}
              className={[
                'h-full rounded-full transition-all',
                income?.worldCapReached ? 'bg-warning' : 'bg-crystal',
              ].join(' ')}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div>
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Unpriced clears
          </p>
          <p className="text-lg font-semibold tabular-nums text-text" data-testid="unpriced-clears">
            {income?.unpricedClears ?? 0}
          </p>
          {income !== undefined && income.unpricedClears > 0 && (
            <p className="mt-0.5 text-xs text-warning underline decoration-dotted decoration-warning/70 underline-offset-2">
              {income.unpricedClears} clear{income.unpricedClears === 1 ? '' : 's'} have unverified value
            </p>
          )}
        </div>
      </div>

      <WeekNav
        weekLabel={weekLabel}
        onPrevWeek={onPrevWeek}
        onNextWeek={onNextWeek}
        onThisWeek={onThisWeek}
        isCurrentWeek={isCurrentWeek}
        canGoNext={canGoNext}
      />
    </div>
  );
}
